<template>
  <div class="management-vessel-inspection-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Vessel Inspection</h3>
        <div class="header-actions">
          <el-button size="small" type="success" plain @click="loadData">
            <el-icon><Refresh /></el-icon> 刷新
          </el-button>
        </div>
      </div>

      <div class="table-wrapper" style="height:550px; overflow:auto;">
        <el-table
          :data="tableData"
          border
          style="width:100%"
          v-loading="loading"
          row-key="orderNo"
        >
          <el-table-column type="index" label="No" width="40" align="center" />
          
          <el-table-column prop="orderNo" label="Order No" width="110" align="center">
            <template #default="{ row }">
              <span class="readonly-text">{{ row.orderNo }}</span>
            </template>
          </el-table-column>
          
          <!-- Customer 左对齐 -->
          <el-table-column prop="customer" label="Customer" width="120">
            <template #default="{ row }">
              <span class="readonly-text text-left">{{ row.customer }}</span>
            </template>
          </el-table-column>
          
          <!-- Vessel 左对齐 -->
          <el-table-column prop="vessel" label="Vessel" width="140">
            <template #default="{ row }">
              <span class="readonly-text text-left">{{ row.vessel }}</span>
            </template>
          </el-table-column>
          
          <!-- Port 左对齐 -->
          <el-table-column prop="port" label="Port" width="110">
            <template #default="{ row }">
              <span class="readonly-text text-left">{{ row.port }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="etb" label="ETB" width="120" align="center">
            <template #default="{ row }">
              <span class="readonly-text">{{ formatDate(row.etb) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="etd" label="ETD" width="120" align="center">
            <template #default="{ row }">
              <span class="readonly-text">{{ formatDate(row.etd) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="pWindow" label="P Window" width="120" align="center">
            <template #default="{ row }">
              <span class="readonly-text">{{ formatDateOnly(row.pWindow) }}</span>
            </template>
          </el-table-column>
          
          <!-- OPS Remarks 左对齐 -->
          <el-table-column prop="opsRemarks" label="OPS Remarks" width="150">
            <template #default="{ row }">
              <span class="readonly-text text-left">{{ row.opsRemarks || '-' }}</span>
            </template>
          </el-table-column>
          
          <!-- MC Remarks 左对齐（只读） -->
          <el-table-column prop="mcRemarks" label="MC Remarks" min-width="200">
            <template #default="{ row }">
              <span class="readonly-text text-left">{{ row.mcRemarks || '-' }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'

// ============ 状态 ============
const loading = ref(false)
const tableData = ref([])

// ============ 工具函数 ============
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const formatDateOnly = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ============ API 配置 ============
const getApiBase = () => process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============ 获取数据 ============
const fetchData = async () => {
  try {
    // 从 order_proceeding 获取 MC 状态为 P 或 A 的订单
    const response = await fetch(`${getApiBase()}/api/auth/order-proceeding`, {
      headers: getHeaders()
    })
    if (response.status === 401) {
      ElMessage.error('Authentication has expired, please log in again')
      setTimeout(() => window.location.href = '/login', 1500)
      return []
    }
    if (!response.ok) throw new Error('Failed to fetch data')
    
    const result = await response.json()
    console.log('获取到的数据:', result)
    
    // 过滤 MC 状态为 P 或 A 的订单
    const filtered = (result || []).filter(item => 
      item.mc === 'P' || item.mc === 'A'
    )
    console.log('✅ 过滤后数据 (MC=P 或 MC=A):', filtered)
    return filtered
  } catch (error) {
    console.error('获取数据错误:', error)
    throw error
  }
}

// ============ 加载数据 ============
const loadData = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const data = await fetchData()
    tableData.value = data.map(item => ({
      orderNo: item.order_no || '',
      customer: item.customer || '',
      vessel: item.vessel || '',
      port: item.port || '',
      etb: item.etb || '',
      etd: item.etd || '',
      pWindow: item.p_window || '',
      opsRemarks: item.ops_remarks || '',
      mcRemarks: item.mc_remarks || '',
      mc: item.mc || '',
      id: item.id
    }))
  } catch (error) {
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// ============ 生命周期 ============
onMounted(() => {
  loadData()
})
</script>

<style scoped>
.management-vessel-inspection-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.table-wrapper {
  height: 550px;
  overflow: auto;
}

:deep(.el-table) {
  font-size: 13px;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

.readonly-text {
  display: inline-block;
  padding: 2px 6px;
  color: #606266;
  font-weight: 400;
  font-size: 12px;
  background: #f5f7fa;
  border-radius: 3px;
  width: 100%;
  text-align: center;
}

/* 左对齐样式 */
.text-left {
  text-align: left !important;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}
</style>